import { ref } from 'vue'
import { sendGraphQLQuery } from '@/services/gql-util'
import { AuditLogEntry, AuditLogFilters } from '@/types/AuditLog'
import axios from 'axios'

export function useAuditLog() {
	const entries = ref<AuditLogEntry[]>([])
	const totalCount = ref(0)
	const loading = ref(false)
	const error = ref<string | null>(null)

	const getAuditLog = async (
		vhost: string,
		filters: AuditLogFilters = {},
		start: number = 0,
		limit: number = 50,
	): Promise<void> => {
		loading.value = true
		error.value = null

		const query = `
		query ($vhost: Int!, $filters: AuditLogFilters, $start: Int, $limit: Int) {
			AuditLog {
				entries(vhost: $vhost, filters: $filters, start: $start, limit: $limit) {
					items {
						id
						userId
						username
						action
						entityType
						entityId
						entityName
						details
						ipAddress
						timestamp
						vhost
					}
					totalCount
				}
			}
		}
		`

		try {
			const data = await sendGraphQLQuery(query, {
				vhost: +vhost,
				filters,
				start,
				limit,
			})
			if (data?.AuditLog?.entries) {
				entries.value = [...data.AuditLog.entries.items]
				totalCount.value = data.AuditLog.entries.totalCount
			}
		} catch (err: any) {
			console.error('Error fetching audit log:', err)
			error.value = err.message || 'Failed to fetch audit log'
			// Fallback to REST
			try {
				const response = await axios.post('/services/json', {
					method: 'audit.getLog',
					vhostID: vhost,
					start,
					limit,
					...filters,
				})
				if (response.data.status !== false) {
					entries.value = response.data.result?.data || []
					totalCount.value = response.data.result?.totalCount || 0
				}
			} catch (restErr) {
				console.error('REST fallback also failed:', restErr)
			}
		} finally {
			loading.value = false
		}
	}

	const getActionTypes = (): string[] => {
		return [
			'login',
			'logout',
			'upload',
			'delete',
			'update',
			'approve',
			'deny',
			'transfer',
			'create',
			'settings_change',
			'permission_change',
		]
	}

	const getEntityTypes = (): string[] => {
		return [
			'media',
			'user',
			'group',
			'channel',
			'collection',
			'application',
			'widget',
			'feed',
			'settings',
		]
	}

	return {
		entries,
		totalCount,
		loading,
		error,
		getAuditLog,
		getActionTypes,
		getEntityTypes,
	}
}
